/**
 * Cutting a floor/ceiling outline (with its openings) into PIECES along drawn lines —
 * shared by the painted finishes (transition lines → finish areas, slabFinish.ts) and
 * the framing zones (framing lines → zones, floor/framing.ts). Each line is cut as a
 * hairline gap, so the pieces meet along it without overlapping.
 */
import type { Pt2 } from '@aj/bim-kernel'
import { differenceClosed, inflateOpen, signedArea, unionClosed } from '../../clipper'

const CUT_HALF_FT = 1 / 2048

export interface SlabPiece { outer: Pt2[]; holes: Pt2[][] }

export function inRing(p: Pt2, ring: Pt2[]): boolean {
  let c = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i], b = ring[j]
    if ((a[1] > p[1]) !== (b[1] > p[1]) && p[0] < ((b[0] - a[0]) * (p[1] - a[1])) / (b[1] - a[1]) + a[0]) c = !c
  }
  return c
}
export function inPiece(piece: SlabPiece, p: Pt2): boolean {
  return inRing(p, piece.outer) && !piece.holes.some((h) => inRing(p, h))
}

/** the outline (any winding) minus its holes, cut along each polyline in `lines` */
export function cutIntoPieces(outerIn: Pt2[], holesIn: Pt2[][], lines: Pt2[][]): SlabPiece[] {
  const outer = signedArea(outerIn) < 0 ? [...outerIn].reverse() : outerIn
  const holes = holesIn.filter((r) => r.length >= 3).map((r) => (signedArea(r) > 0 ? [...r].reverse() : r))
  const cuts = lines.filter((l) => l?.length >= 2).flatMap((l) => inflateOpen(l, CUT_HALF_FT, 2))
  const rings = cuts.length ? differenceClosed([outer, ...holes], unionClosed(cuts)) : [outer, ...holes]
  const outs = rings.filter((r) => signedArea(r) > 1e-9)
  const hs = rings.filter((r) => signedArea(r) < -1e-9)
  const pieces: SlabPiece[] = outs.map((o) => ({ outer: o, holes: [] }))
  for (const h of hs) {
    const host = pieces.find((p) => inRing(h[0], p.outer))
    if (host) host.holes.push(h)
  }
  return pieces
}
